<script lang="ts">
    import type { TextTone } from "@/types/UITypes";
    import classNames from "classnames";

    type VariantBase =
        | "heading-3xl"
        | "heading-2xl"
        | "heading-xl"
        | "heading-lg"
        | "heading-md"
        | "heading-sm"
        | "body-xlg"
        | "body-lg"
        | "body-md"
        | "body-sm"
        | "body-xs"
        | "body-xxs";

    type FontWeight = "medium" | "semibold";
    type Component = "p" | "span" | "h1" | "h2" | "div";

    export let variant: VariantBase = "body-lg";
    export let component: Component = "p";
    export let underline = false;
    export let fontWeight: FontWeight | null = null;
    export let tone: TextTone = "base";
    export let visuallyHidden = false;
    let className: string | undefined = undefined;
    export { className as class };
</script>

<svelte:element
    this={component}
    class={classNames(
        "gw-text",
        !visuallyHidden
            ? [
                  `gw-text--${variant}`,
                  `gw-text--tone-${tone}`,
                  underline && "gw-text--underline",
                  fontWeight && `gw-text--${fontWeight}`,
              ]
            : "gw-text--visually-hidden",
        className
    )}
    {...$$restProps}
>
    <slot />
</svelte:element>

<style lang="postcss">
    @define-mixin text-variant $name {
        font-size: var(--gw-text-$(name)-font-size);
        line-height: var(--gw-text-$(name)-font-line-height);
        font-weight: var(--gw-text-$(name)-font-weight);
        letter-spacing: var(--gw-text-$(name)-font-letter-spacing);
    }

    .gw-text {
        margin: 0;

        &--heading-3xl {
            @mixin text-variant heading-3xl;
        }

        &--heading-2xl {
            @mixin text-variant heading-2xl;
        }

        &--heading-xl {
            @mixin text-variant heading-xl;
        }

        &--heading-lg {
            @mixin text-variant heading-lg;
        }

        &--heading-md {
            @mixin text-variant heading-md;
        }

        &--heading-sm {
            @mixin text-variant heading-sm;
        }

        &--body-xlg {
            @mixin text-variant body-xlg;
        }

        &--body-lg {
            @mixin text-variant body-lg;
        }

        &--body-md {
            @mixin text-variant body-md;
        }

        &--body-sm {
            @mixin text-variant body-sm;
        }

        &--body-xs {
            @mixin text-variant body-xs;
        }

        &--body-xxs {
            @mixin text-variant body-xxs;
        }

        &--underline {
            text-decoration: underline;
        }

        &--medium {
            font-weight: var(--gw-font-weight-medium);
        }

        &--semibold {
            font-weight: var(--gw-font-weight-semibold);
        }

        &--tone-base {
            color: var(--gw-text-primary);
        }

        &--tone-subdued {
            color: var(--gw-text-secondary);
        }

        &--tone-critical {
            color: var(--gw-text-critical);
        }

        &--tone-tertiary {
            color: var(--gw-text-tertiary);
        }

        &--tone-disabled {
            color: var(--gw-text-primary-disabled);
        }

        &--tone-emphasis {
            color: var(--gw-text-emphasis);
        }

        &--tone-inherit {
            color: inherit;
        }

        &--visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: 0;
            overflow: hidden;
            clip: rect(0 0 0 0);
            clip-path: inset(50%);
            white-space: nowrap;
            border: 0;
        }
    }
</style>
